import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../lib/supabase";
import { MapPin, MessageSquare, Clock, CheckCircle, XCircle } from "lucide-react";
import { formatDateNZ } from "../lib/format";

const MyQuotesPage = () => {
  const { isAuthenticated, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchQuotes();
  }, [isAuthenticated, authLoading]);

  const fetchQuotes = async () => {
    try {
      const response = await fetch(`${API_URL}/quotes`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const data = await response.json();
      setQuotes(Array.isArray(data) ? data : []);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const STATUS = {
    'new': { label: 'Submitted', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
    'sent': { label: 'Sent to Providers', color: '#6366f1', bg: '#e0e7ff', icon: MessageSquare },
    'responded': { label: 'Responses In', color: '#065f46', bg: '#d1fae5', icon: MessageSquare },
    'closed': { label: 'Completed', color: '#047857', bg: '#d1fae5', icon: CheckCircle },
    'canceled': { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6', icon: XCircle },
  };
  const sc = (s) => STATUS[s] || { label: s, color: '#6b7280', bg: '#f3f4f6', icon: Clock };

  const tabs = [
    { key: 'all', label: 'All Quotes' },
    { key: 'new', label: 'Submitted' },
    { key: 'responded', label: 'Responses In' },
    { key: 'closed', label: 'Completed' },
  ];
  const filtered = filter === 'all' ? quotes : quotes.filter(q => q.status === filter);

  // Stats
  const active = quotes.filter(q => ['new', 'sent', 'responded'].includes(q.status)).length;
  const withResponses = quotes.filter(q => (q.responses || []).length > 0).length;
  const completed = quotes.filter(q => q.status === 'closed').length;
  const totalResponses = quotes.reduce((sum, q) => sum + (q.responses || []).length, 0);

  if (authLoading || loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page my-quotes-page" data-testid="my-quotes-page">
      <div className="page-header">
        <h1>My Quote Requests</h1>
        <p>Track your requests and compare provider responses</p>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Active Quotes', val: active, color: '#f59e0b' },
          { label: 'Responses', val: totalResponses, color: '#065f46' },
          { label: 'Completed', val: completed, color: '#047857' },
          { label: 'Total Quotes', val: quotes.length, color: 'var(--navy)' },
        ].map(s => (
          <div key={s.label} style={{ padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', borderLeft: `3px solid ${s.color}` }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)' }} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>{s.val}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            data-testid={`tab-${t.key}`}
            style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid var(--border)', fontSize: '0.8125rem', fontWeight: filter === t.key ? 600 : 400, background: filter === t.key ? 'var(--navy)' : 'var(--surface)', color: filter === t.key ? '#fff' : 'var(--text-dark)', cursor: 'pointer' }}>
            {t.label} {t.key !== 'all' && <span style={{ opacity: 0.7 }}>({quotes.filter(q => q.status === t.key).length})</span>}
          </button>
        ))}
      </div>

      {/* Quote list */}
      {filtered.length > 0 ? (
        <div className="quotes-list" data-testid="quotes-list" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {filtered.map(quote => {
            const cfg = sc(quote.status);
            const responses = quote.responses || [];
            const StatusIcon = cfg.icon;
            return (
              <div key={quote.id} className="quote-card" data-testid={`quote-${quote.id}`}
                style={{ padding: '18px 20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', borderLeft: `3px solid ${cfg.color}`, cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                onClick={() => navigate(`/my-quotes/${quote.id}`)}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-dark)', margin: 0 }}>{quote.services?.name || 'Service Request'}</h3>
                  <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, color: cfg.color, background: cfg.bg, display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    <StatusIcon size={12} /> {cfg.label}
                  </span>
                </div>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: '10px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{quote.job_description}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: '14px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MapPin size={12} /> {quote.city}</span>
                    <span>{formatDateNZ(quote.created_at)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', fontSize: '0.75rem' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{quote.providers_matched || 0} matched</span>
                    {responses.length > 0 && (
                      <span style={{ color: '#065f46', fontWeight: 600 }}>{responses.length} response{responses.length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state" data-testid="empty-quotes">
          <p>{filter === 'all' ? "You haven't submitted any quote requests yet." : `No ${tabs.find(t => t.key === filter)?.label.toLowerCase()} quotes.`}</p>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 16 }}>Request quotes from verified local providers — free with no obligation.</p>
          <Link to="/services" className="btn-primary">Browse Services</Link>
        </div>
      )}
    </div>
  );
};


export default MyQuotesPage;
